import React, { useEffect, useRef, useState } from "react";
import { useVideoPlayer } from "./_use-video-player";
import { VideoSkeleton } from "./_video-skeleton";

interface AutoplayVideoProps {
  src: string;
  poster?: string;
  loop?: boolean;
  className?: string;
  onEnded?: () => void;
  portrait?: boolean;
  lazy?: boolean;
}

export function AutoplayVideo({
  src,
  poster,
  loop = true,
  className = "absolute inset-0 w-full h-full",
  onEnded,
  portrait = false,
  lazy = false,
}: AutoplayVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(!lazy);

  useEffect(() => {
    if (!lazy || !containerRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsVisible(Boolean(entry?.isIntersecting)),
      { rootMargin: "200px 0px", threshold: 0.1 },
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [lazy]);

  const { videoRef, isLoading } = useVideoPlayer({
    src: isVisible ? src : null,
    poster,
    loop,
    muted: true,
    paused: !isVisible,
    onEnded,
  });

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {isVisible && (
        <video
          ref={videoRef}
          muted
          playsInline
          preload="metadata"
          className={`w-full h-full ${portrait ? "object-contain" : "object-cover"}`}
        />
      )}
      {!isVisible && poster && (
        <img src={poster} alt="" className="w-full h-full object-cover" loading="lazy" />
      )}
      {!isVisible && !poster && (
        <div className="w-full h-full bg-slate-900" />
      )}
      {isVisible && <VideoSkeleton isLoading={isLoading} />}
    </div>
  );
}

interface VideoHeroProps {
  src: string;
  poster?: string;
  portrait?: boolean;
  aspectClass?: string;
  children?: React.ReactNode;
}

export function VideoHero({
  src,
  poster,
  portrait = false,
  aspectClass = "aspect-[16/10] sm:aspect-[16/9] max-h-[78vh]",
  children,
}: VideoHeroProps) {
  const { videoRef, isLoading } = useVideoPlayer({
    src,
    poster,
    muted: true,
    loop: true,
  });

  return (
    <div className={`relative w-full bg-black overflow-hidden ${aspectClass}`}>
      <video
        ref={videoRef}
        muted
        playsInline
        preload="auto"
        className={`absolute inset-0 w-full h-full ${portrait ? "object-contain" : "object-cover"}`}
      />

      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" />

      <VideoSkeleton isLoading={isLoading} />

      {children}
    </div>
  );
}

interface VideoModalProps {
  video: {
    videoUrl: string;
    posterUrl: string;
    categoryLabel: string;
    title: string;
  };
  onClose: () => void;
}

export function VideoModal({ video, onClose }: VideoModalProps) {
  const { videoRef, isLoading } = useVideoPlayer({
    src: video.videoUrl,
    poster: video.posterUrl,
    autoplay: true,
    loop: false,
    muted: false,
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 sm:p-8">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition text-xl"
      >
        ✕
      </button>
      <div className="w-full max-w-6xl">
        <div className="relative aspect-video rounded-sm bg-black overflow-hidden">
          <video
            ref={videoRef}
            controls
            className="w-full h-full"
          />
          <VideoSkeleton isLoading={isLoading} />
        </div>
        <div className="text-white mt-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-1">
            {video.categoryLabel}
          </div>
          <div className="font-serif text-xl">{video.title}</div>
        </div>
      </div>
    </div>
  );
}

